// src/api/axios.js
import axios from 'axios';
import store from '@/store';

const baseURL = process.env.VUE_APP_API_BASE_URL + '/api/v1';
// const baseURL = 'https://stg-api.shooohei.com/api/v1';
// const baseURL = 'https://prod-api.shooohei.com/api/v1';
// const baseURL = 'https://stg-measure-report-mng-api.soei-planning.com/api/v1';
// const baseURL = 'https://measure-report-mng-api.soei-planning.com/api/v1';

const instance = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

const hasStoredAuth = () => {
  const accessToken = localStorage.getItem('accessToken');
  const client = localStorage.getItem('client');
  const uid = localStorage.getItem('uid');

  return Boolean(accessToken && client && uid);
};

const syncStoreAuthState = (isLoggedIn) => {
  store.dispatch('setLoggedIn', isLoggedIn);
  store.dispatch('setUserName', isLoggedIn ? localStorage.getItem('userName') : null);
  store.dispatch('setUserRole', isLoggedIn ? localStorage.getItem('userRole') : null);
};

const clearAuthStorage = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('client');
  localStorage.removeItem('uid');
  localStorage.removeItem('userId');
  localStorage.removeItem('userName');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userRolesBySystem');
  localStorage.removeItem('userExplicitRolesBySystem');
};

const saveAuthHeaders = (headers = {}) => {
  const accessToken = headers['access-token'];
  const client = headers['client'];
  const uid = headers['uid'];

  if (accessToken && client && uid) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('client', client);
    localStorage.setItem('uid', uid);
  }
};

const saveRoleMappings = (responseData = {}) => {
  if (responseData.roles_by_system && typeof responseData.roles_by_system === 'object') {
    localStorage.setItem('userRolesBySystem', JSON.stringify(responseData.roles_by_system));
  } else {
    localStorage.removeItem('userRolesBySystem');
  }

  if (responseData.explicit_roles_by_system && typeof responseData.explicit_roles_by_system === 'object') {
    localStorage.setItem('userExplicitRolesBySystem', JSON.stringify(responseData.explicit_roles_by_system));
  } else {
    localStorage.removeItem('userExplicitRolesBySystem');
  }
};

const saveUserProfile = (responseData = {}) => {
  localStorage.setItem('userId', responseData.id);
  localStorage.setItem('userName', responseData.name);
  localStorage.setItem('userRole', responseData.role);
  saveRoleMappings(responseData);
};

const shouldTrackGlobalLoading = (config = {}) => {
  return !config?.meta?.skipGlobalLoading;
};

const isSystemAccessDenied = (error) => {
  if (error?.response?.status !== 403) return false;

  const data = error.response?.data || {};
  const firstError = Array.isArray(data.errors) ? data.errors[0] : null;
  const message = firstError || data.error || data.message;
  return typeof message === 'string' && message.includes('アクセス権限');
};

// ログインメソッド
export const login = (email, password) => {
  return instance.post('/auth/sign_in', { email, password })
    .then(response => {
      // レスポンスヘッダーからトークン情報を取得して保存
      // ユーザー情報の保存
      saveUserProfile(response.data.data || {});
      saveAuthHeaders(response.headers);
      syncStoreAuthState(true);
      return response;
    });
};

// Measure -> Billing のログイン引き継ぎトークン消費
export const consumeSystemHandoff = (handoffToken) => {
  return instance.post('/auth/system_handoffs/consume', { handoff_token: handoffToken })
    .then(response => {
      saveUserProfile(response.data.data || {});
      saveAuthHeaders(response.headers);
      syncStoreAuthState(true);
      return response;
    });
};

// ログアウトメソッド
export const logout = () => {
  return instance.delete('/auth/sign_out')
    .then(response => {
      clearAuthStorage();
      syncStoreAuthState(false);
      return response;
    });
};

// システム間ログイン引き継ぎトークン発行
export const createSystemHandoff = (targetClientSystem) => {
  return instance.post('/auth/system_handoffs', {
    target_client_system: targetClientSystem
  });
};

// パスワードリセットメソッド
export const passwordUpdate = (passwordData) => {
  // const userId = localStorage.getItem('userId');
  const userId = passwordData.user_id;
  return instance.post(`/users/${userId}/password_update`, {user: passwordData});
};

// ユーザ情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
export const getUsers = (params = {}) => {
  return instance.get('/users', { params });
};

export const getUserRoleCatalog = () => {
  return instance.get('/users/role_catalog');
};

// selectbox用
export const getSelectBoxUsers = () => {
  return instance.get('/users/select_all');
};

const buildUserFormData = (userData) => {
  const formData = new FormData();
  ['name', 'email', 'role', 'password'].forEach((field) => {
    if (userData[field] !== undefined && userData[field] !== null) {
      formData.append(`user[${field}]`, userData[field]);
    }
  });
  Object.entries(userData.roles_by_system || {}).forEach(([system, role]) => {
    formData.append(`user[roles_by_system][${system}]`, role);
  });
  if (userData.employee_seal_image) {
    formData.append('user[employee_seal_image]', userData.employee_seal_image);
  }
  if (userData.remove_employee_seal_image) {
    formData.append('user[remove_employee_seal_image]', 'true');
  }
  return formData;
};

const userRequestBody = (userData) => {
  const usesFileUpload = Boolean(
    userData.employee_seal_image || userData.remove_employee_seal_image
  );
  if (!usesFileUpload) return { data: { user: userData }, config: undefined };

  return {
    data: buildUserFormData(userData),
    config: { headers: { 'Content-Type': 'multipart/form-data' } }
  };
};

export const createUser = (userData) => {
  const request = userRequestBody(userData);
  return instance.post('/users', request.data, request.config);
};

export const updateUser = (userId, userData) => {
  const request = userRequestBody(userData);
  return instance.put(`/users/${userId}`, request.data, request.config);
};

export const getUser = (userId) => {
  return instance.get(`/users/${userId}`);
};

export const deleteUser = (userId) => {
  return instance.delete(`/users/${userId}`);
};

export const restoreUser = (userId) => {
  return instance.put(`/users/${userId}/restore`);
};

// CSVエクスポート
export const exportUsersCsv = (params = {}) => {
  return instance.get('/users/csv_output', {
    params,
    responseType: 'blob'
  });
};

// CSVインポート
export const importUsersCsv = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return instance.post('users/csv_upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
// ユーザ情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝


// 顧客情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
export const getCustomers = (params = {}) => {
  return instance.get('/m_customers', { params });
};

export const checkCustomerDuplicate = (name) => {
  return instance.get('/m_customers/duplicate_check', { params: { name } });
};

// selectbox用
export const getSelectBoxCustomers = () => {
  return instance.get('/m_customers/select_all');
};

export const createCustomer = (customerData) => {
  const dataToSend = customerData;
  return instance.post('/m_customers', {m_customer: dataToSend});
};

export const updateCustomer = (customerId, customerData) => {
  const dataToSend = customerData;
  return instance.put(`/m_customers/${customerId}`, { m_customer: dataToSend });
};

export const getCustomer = (customerId, params = {}) => {
  return instance.get(`/m_customers/${customerId}`, { params });
};

export const deleteCustomer = (customerId) => {
  return instance.delete(`/m_customers/${customerId}`);
};

export const restoreCustomer = (customerId) => {
  return instance.put(`/m_customers/${customerId}/restore`);
};

// CSVエクスポート
export const exportCustomersCsv = (params = {}) => {
  return instance.get('/m_customers/csv_output', {
    params,
    responseType: 'blob'
  });
};

// CSVインポート
export const importCustomersCsv = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return instance.post('m_customers/csv_upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

// 顧客 + 建物の取得
export const getCustomersBuildings = () => {
  return instance.get('/m_customers/customers_and_buildings');
};
// 顧客情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝

// 銀行マスタAPI＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
export const getSelectBoxBanks = () => {
  return instance.get('/m_banks/select_all');
};
// 銀行マスタAPI＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝


// 顧客担当者情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
export const getCustomerRepresentatives = (params = {}) => {
  return instance.get('/m_customer_representatives', { params });
};

// selectbox用
export const getSelectBoxCustomerRepresentatives = () => {
  return instance.get('/m_customer_representatives/select_all');
};

// 予定表送付先顧客担当者selectbox用
export const getCalSelectBoxCustomerRepresentatives = () => {
  return instance.get('/m_customer_representatives/only_cal_destination_address_use_flg_lists');
};

// 請求書送付先顧客担当者selectbox用
export const getBillSelectBoxCustomerRepresentatives = () => {
  return instance.get('/m_customer_representatives/only_bill_destination_address_use_flg_lists');
};

export const createCustomerRepresentative = (customerRepresentativeData) => {
  const dataToSend = customerRepresentativeData;
  return instance.post('/m_customer_representatives', {m_customer_representative: dataToSend});
};

export const updateCustomerRepresentative = (customerRepresentativeId, customerRepresentativeData) => {
  const dataToSend = customerRepresentativeData;
  return instance.put(`/m_customer_representatives/${customerRepresentativeId}`, { m_customer_representative: dataToSend });
};

export const getCustomerRepresentative = (customerRepresentativeId) => {
  return instance.get(`/m_customer_representatives/${customerRepresentativeId}`);
};

// 顧客IDから顧客担当者情報を取得（請求用）
export const getBillingCustomerRepresentative = (customerId) => {
  return instance.get('/m_customer_representatives/billing_target', {
    params: { customer_id: customerId }
  });
};

export const getFilterCustomerRepresentative = (vendorId) => {
  return instance.get(`/m_customer_representatives/${vendorId}/filter_vendor_representatives`);
};

export const deleteCustomerRepresentative = (customerRepresentativeId) => {
  return instance.delete(`/m_customer_representatives/${customerRepresentativeId}`);
};

export const restoreCustomerRepresentative = (customerRepresentativeId) => {
  return instance.put(`/m_customer_representatives/${customerRepresentativeId}/restore`);
};


// CSVエクスポート
export const exportCustomerRepresentativesCsv = (params = {}) => {
  return instance.get('/m_customer_representatives/csv_output', {
    params,
    responseType: 'blob'
  });
};

// CSVインポート
export const importCustomerRepresentativesCsv = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return instance.post('m_customer_representatives/csv_upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
// 顧客担当者情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝


// 建物情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
export const getBuildings = (params = {}) => {
  return instance.get('/m_buildings', { params });
};

export const checkBuildingDuplicate = (name) => {
  return instance.get('/m_buildings/duplicate_check', { params: { name } });
};

export const getAnnualScheduleBuildings = (params = {}) => {
  return instance.get('/m_buildings/annual_schedule_buildings', { params });
};

// selectbox用
export const getSelectBoxBuildings = () => {
  return instance.get('/m_buildings/select_all');
};

export const createBuilding = (buildingData) => {
  const dataToSend = buildingData;
  return instance.post('/m_buildings', {m_building: dataToSend});
};

export const updateBuilding = (buildingId, buildingData, customerUpdateFrom = null) => {
  const dataToSend = buildingData;
  const payload = { m_building: dataToSend };
  if (customerUpdateFrom) {
    payload.customer_update_from = customerUpdateFrom;
  }
  return instance.put(`/m_buildings/${buildingId}`, payload);
};

export const getBuilding = (buildingId, params = {}) => {
  return instance.get(`/m_buildings/${buildingId}`, { params });
};

export const deleteBuilding = (buildingId) => {
  return instance.delete(`/m_buildings/${buildingId}`);
};

export const restoreBuilding = (buildingId) => {
  return instance.put(`/m_buildings/${buildingId}/restore`);
};

// CSVエクスポート
export const exportBuildingsCsv = (params = {}) => {
  return instance.get('/m_buildings/csv_output', {
    params,
    responseType: 'blob'
  });
};

// CSVインポート
export const importBuildingsCsv = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return instance.post('m_buildings/csv_upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
// 建物情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝


// 建物種別情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
export const getBuildingTypes = (params = {}) => {
  return instance.get('/m_building_types', { params });
};

export const checkBuildingTypeDuplicate = (name) => {
  return instance.get('/m_building_types/duplicate_check', { params: { name } });
};

export const createBuildingType = (buildingTypeData) => {
  const dataToSend = {
    building_type_nm: buildingTypeData.building_type_nm,
    sort_no: buildingTypeData.sort_no
  };
  return instance.post('/m_building_types', {m_building_type: dataToSend});
};

export const updateBuildingType = (buildingTypeId, buildingTypeData) => {
  const dataToSend = {
    building_type_nm: buildingTypeData.building_type_nm,
    sort_no: buildingTypeData.sort_no
  };
  return instance.put(`/m_building_types/${buildingTypeId}`, { m_building_type: dataToSend });
};

export const getBuildingType = (buildingTypeId, params = {}) => {
  return instance.get(`/m_building_types/${buildingTypeId}`, { params });
};

export const deleteBuildingType = (buildingTypeId) => {
  return instance.delete(`/m_building_types/${buildingTypeId}`);
};

export const restoreBuildingType = (buildingTypeId) => {
  return instance.put(`/m_building_types/${buildingTypeId}/restore`);
};

// CSVエクスポート
export const exportBuildingTypesCsv = (params = {}) => {
  return instance.get('/m_building_types/csv_output', {
    params,
    responseType: 'blob'
  });
};

// CSVインポート
export const importBuildingTypesCsv = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return instance.post('m_building_types/csv_upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
// 建物種別情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝

// マスタ表示制御API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
export const getMasterFieldVisibilities = (masterType, clientSystem) => {
  return instance.get('/master_field_visibilities', {
    params: {
      master_type: masterType,
      client_system: clientSystem
    }
  });
};

export const updateMasterFieldVisibilities = (masterType, clientSystem, fields) => {
  return instance.put('/master_field_visibilities/bulk_upsert', {
    master_type: masterType,
    client_system: clientSystem,
    fields
  });
};

export const getMasterRecordVisibilities = (masterType, clientSystem, recordIds = []) => {
  return instance.get('/master_record_visibilities', {
    params: {
      master_type: masterType,
      client_system: clientSystem,
      record_ids: Array.isArray(recordIds) ? recordIds.join(',') : recordIds
    }
  });
};

export const updateMasterRecordVisibility = (masterType, clientSystem, recordId, visible) => {
  return instance.put('/master_record_visibilities/upsert', {
    master_type: masterType,
    client_system: clientSystem,
    record_id: recordId,
    visible
  });
};
// マスタ表示制御API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝


// 業者情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
export const getVendors = (params = {}) => {
  return instance.get('/m_vendors', { params });
};

// selectbox用
export const getSelectBoxVendors = () => {
  return instance.get('/m_vendors/select_all');
};

export const createVendor = (vendorData) => {
  const dataToSend = vendorData;
  return instance.post('/m_vendors', {m_vendor: dataToSend});
};

export const updateVendor = (vendorId, vendorData) => {
  const dataToSend = vendorData;
  return instance.put(`/m_vendors/${vendorId}`, { m_vendor: dataToSend });
};

export const getVendor = (vendorId) => {
  return instance.get(`/m_vendors/${vendorId}`);
};

export const deleteVendor = (vendorId) => {
  return instance.delete(`/m_vendors/${vendorId}`);
};

export const restoreVendor = (vendorId) => {
  return instance.put(`/m_vendors/${vendorId}/restore`);
};

// CSVエクスポート
export const exportVendorsCsv = (params = {}) => {
  return instance.get('/m_vendors/csv_output', {
    params,
    responseType: 'blob'
  });
};

// CSVインポート
export const importVendorsCsv = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return instance.post('m_vendors/csv_upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
// 業者情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝


// 業務区分情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
export const getBusinessClassifications = (params = {}) => {
  return instance.get('/m_business_classifications', { params });
};

export const createBusinessClassification = (businessClassificationData) => {
  const dataToSend = {
    business_classification_nm: businessClassificationData.business_classification_nm,
    sort_no: businessClassificationData.sort_no,
  };
  return instance.post('/m_business_classifications', {m_business_classification: dataToSend});
};

export const updateBusinessClassification = (businessClassificationId, businessClassificationData) => {
  const dataToSend = {
    business_classification_nm: businessClassificationData.business_classification_nm,
    sort_no: businessClassificationData.sort_no,
  };
  return instance.put(`/m_business_classifications/${businessClassificationId}`, { m_business_classification: dataToSend });
};

export const getBusinessClassification = (businessClassificationId) => {
  return instance.get(`/m_business_classifications/${businessClassificationId}`);
};

export const deleteBusinessClassification = (businessClassificationId) => {
  return instance.delete(`/m_business_classifications/${businessClassificationId}`);
};

export const restoreBusinessClassification = (businessClassificationId) => {
  return instance.put(`/m_business_classifications/${businessClassificationId}/restore`);
};

export const exportBusinessClassificationsCsv = (params = {}) => {
  return instance.get('/m_business_classifications/csv_output', {
    params,
    responseType: 'blob'
  });
};

export const importBusinessClassificationsCsv = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return instance.post('m_business_classifications/csv_upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
// 業務区分情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝

// 見積管理API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
export const getEstimates = (params = {}) => {
  return instance.get('/estimates', { params });
};

export const getEstimateApprovalPendingCount = () => {
  return instance.get('/estimates/approval_pending_count', {
    // 定期更新で画面全体のローディングを表示しない。
    meta: { skipGlobalLoading: true }
  });
};

export const getEstimate = (estimateId, params = {}) => {
  return instance.get(`/estimates/${estimateId}`, { params });
};

export const getEstimateSubcategorySuggestions = (params = {}) => {
  return instance.get('/estimates/subcategory_suggestions', { params });
};

export const getEstimateItemNameSuggestions = (params = {}) => {
  return instance.get('/estimates/item_name_suggestions', {
    params,
    // 入力補完の通信では画面操作を遮る全画面ローディングを表示しない。
    meta: { skipGlobalLoading: true }
  });
};

export const createEstimate = (estimateData) => {
  return instance.post('/estimates', { estimate: estimateData });
};

export const updateEstimate = (estimateId, estimateData) => {
  return instance.patch(`/estimates/${estimateId}`, { estimate: estimateData });
};

export const deleteEstimate = (estimateId, lockVersion) => {
  return instance.delete(`/estimates/${estimateId}`, {
    data: { lock_version: lockVersion }
  });
};

export const restoreEstimate = (estimateId, lockVersion) => {
  return instance.put(`/estimates/${estimateId}/restore`, {
    lock_version: lockVersion
  });
};

export const copyEstimate = (estimateId, lockVersion) => {
  return instance.post(`/estimates/${estimateId}/copy`, {
    lock_version: lockVersion
  });
};

export const reviseEstimate = (estimateId, lockVersion) => {
  return instance.post(`/estimates/${estimateId}/revise`, {
    lock_version: lockVersion
  });
};

export const getEstimateWorkConversionPreview = estimateId => {
  return instance.get(`/estimates/${estimateId}/work_conversion_preview`);
};

export const convertEstimateToWork = (estimateId, lockVersion, workConversions) => {
  return instance.post(`/estimates/${estimateId}/convert_to_work`, {
    lock_version: lockVersion,
    work_conversions: workConversions
  });
};

export const getEstimateDiff = (estimateId, baseId) => {
  return instance.get(`/estimates/${estimateId}/diff`, {
    params: { base_id: baseId }
  });
};

export const createEstimatePdfHistory = (
  estimateId,
  pdfFile,
  outputLayoutType = 'portrait_simple',
  languageType = 'japanese'
) => {
  const formData = new FormData();
  formData.append('pdf_file', pdfFile);
  formData.append('output_layout_type', outputLayoutType);
  formData.append('language_type', languageType);

  return instance.post(`/estimates/${estimateId}/pdf_histories`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const downloadEstimatePdfHistory = (estimateId, pdfHistoryId) => {
  return instance.get(
    `/estimates/${estimateId}/pdf_histories/${pdfHistoryId}/download`,
    { responseType: 'blob' }
  );
};

const transitionEstimate = (estimateId, action, payload = {}) => {
  return instance.patch(`/estimates/${estimateId}/${action}`, payload);
};

export const requestEstimateApproval = (estimateId, lockVersion, comment = null) => {
  return transitionEstimate(estimateId, 'request_approval', {
    lock_version: lockVersion,
    comment
  });
};

export const approveEstimate = (estimateId, lockVersion, comment = null) => {
  return transitionEstimate(estimateId, 'approve', {
    lock_version: lockVersion,
    comment
  });
};

export const rejectEstimate = (estimateId, lockVersion, comment = null) => {
  return transitionEstimate(estimateId, 'reject', {
    lock_version: lockVersion,
    comment
  });
};

export const presentEstimate = (estimateId, lockVersion) => {
  return transitionEstimate(estimateId, 'present', {
    lock_version: lockVersion
  });
};

export const orderEstimate = (estimateId, lockVersion) => {
  return transitionEstimate(estimateId, 'order', {
    lock_version: lockVersion
  });
};

export const markEstimateLost = (estimateId, lockVersion, reason, reasonCategory = null) => {
  return transitionEstimate(estimateId, 'mark_lost', {
    lock_version: lockVersion,
    reason,
    reason_category: reasonCategory
  });
};

export const unlockEstimateApproval = (estimateId, lockVersion, reason) => {
  return transitionEstimate(estimateId, 'unlock_approval', {
    lock_version: lockVersion,
    reason
  });
};

export const getEstimateSubcategories = (params = {}) => {
  return instance.get('/estimate_subcategories', { params });
};

export const getEstimateSubcategoryOptions = () => {
  return instance.get('/estimate_subcategories/select_options');
};

export const getEstimateSubcategory = (estimateSubcategoryId) => {
  return instance.get(`/estimate_subcategories/${estimateSubcategoryId}`);
};

export const createEstimateSubcategory = (estimateSubcategoryData) => {
  return instance.post('/estimate_subcategories', {
    estimate_subcategory: estimateSubcategoryData
  });
};

export const updateEstimateSubcategory = (estimateSubcategoryId, estimateSubcategoryData) => {
  return instance.patch(`/estimate_subcategories/${estimateSubcategoryId}`, {
    estimate_subcategory: estimateSubcategoryData
  });
};

export const deleteEstimateSubcategory = (estimateSubcategoryId) => {
  return instance.delete(`/estimate_subcategories/${estimateSubcategoryId}`);
};

export const restoreEstimateSubcategory = (estimateSubcategoryId) => {
  return instance.put(`/estimate_subcategories/${estimateSubcategoryId}/restore`);
};
// 見積管理API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝


// 年間予定業務API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
export const getMonthlyAnnualSchedules = (params = {}) => {
  return instance.get('/t_building_work_annual_schedules/monthly_annual_work_schedules', { params });
};

// 建物IDと年を指定して年間予定表を取得する関数
export const getBuildingAnnualSchedules = (buildingId, year, month) => {
  return instance.get('/t_building_work_annual_schedules', {
    params: {
      building_id: buildingId,
      year: year,
      month: month
    }
  });
};

export const getBuildingAnnualSchedule = (scheduleId) => {
  return instance.get(`/t_building_work_annual_schedules/${scheduleId}`);
};

// 年間予定（業務項目）と作業予定詳細を新規作成するAPI呼び出し
export const createAnnualScheduleWithDetails = (annualScheduleData, scheduleDetails) => {
  return instance.post('/t_building_work_annual_schedules', {
    annual_schedule: annualScheduleData,
    schedule_details: scheduleDetails // 月別の詳細データを一括送信
  });
};


// 年間予定（業務項目）と作業予定詳細を更新するAPI呼び出し
export const updateAnnualScheduleWithDetails = (scheduleId, annualScheduleData, scheduleDetails) => {
  return instance.put(`/t_building_work_annual_schedules/${scheduleId}`, {
    annual_schedule: annualScheduleData,
    schedule_details: scheduleDetails // 月別の詳細データを一括送信
  });
};

// 年間予定情報削除
export const deleteBuildingAnnualSchedule = (scheduleId) => {
  return instance.delete(`/t_building_work_annual_schedules/${scheduleId}`);
};

// 年間予定詳細と年間予定詳細変更情報を取得
export const getAnnualWorkDetailAndChangeInfo = (scheduleDetailId, targetWorkYear, targetWorkMonth) => {
  return instance.get(`/t_building_work_annual_schedules/${scheduleDetailId}/show_schedule_detail_with_change_info`, {
    params: {
      target_work_year: targetWorkYear,
      target_work_month: targetWorkMonth
    }
  });
};

// 年間予定作業の並び順更新
export const updateScheduleSortOrder = (payload) => {
  return instance.put('/t_building_work_annual_schedules/update_sort_order', {
    schedules: payload
  });
};

// 年間予定（業務項目）と作業予定詳細を修正するAPI呼び出し
export const fixScheduleDetailChangeInfo = (ScheduleDetailChangeData) => {
  return instance.post('/t_building_work_annual_schedules/fix_schedule_detail_change', {
    t_building_work_schedule_detail_change_info: ScheduleDetailChangeData // 月別の詳細データを一括送信
  });
};
// 年間予定業務API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝


// 臨時作業情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
export const getTemporaryWorks = (params = {}) => {
  return instance.get('/t_temporary_works', { params });
};

export const getUnconfirmTemporaryWorks = (params = {}) => {
  return instance.get('/t_temporary_works/unconfirm_temporary_works', { params });
};

export const getTemporaryWorkBiilingCount = () => {
  return instance.get('/t_temporary_works/get_billing_count');
};

export const createTemporaryWork = (temporaryWorkData) => {
  return instance.post('/t_temporary_works', {
    t_temporary_work: temporaryWorkData
  });
};

export const updateTemporaryWork = (temporaryWorkId, temporaryWorkData) => {
  return instance.put(`/t_temporary_works/${temporaryWorkId}`, {
    t_temporary_work: temporaryWorkData
  });
};

export const getTemporaryWork = (temporaryWorkId) => {
  return instance.get(`/t_temporary_works/${temporaryWorkId}`);
};

export const deleteTemporaryWork = (temporaryWorkId) => {
  return instance.delete(`/t_temporary_works/${temporaryWorkId}`);
};

// 総務チェック（業者請求確認）
export const generalAffairsVendorCheck = (temporaryWorkId, temporaryWorkData) => {
  return instance.put(`/t_temporary_works/${temporaryWorkId}/general_affairs_vendor_check_regist`, {
    t_temporary_work: {
      confirm_vendor_billing: temporaryWorkData.confirm_vendor_billing,
      vendor_billing_confirm_dt: temporaryWorkData.vendor_billing_confirm_dt
    },
  });
};

// 総務チェック（業者請求確認）解除
export const generalAffairsVendorCancel = (temporaryWorkId) => {
  return instance.put(`/t_temporary_works/${temporaryWorkId}/general_affairs_vendor_cancel_regist`);
};

// 作業実施確認
export const workCompleteCheck = (temporaryWorkId, temporaryWorkData) => {
  return instance.put(`/t_temporary_works/${temporaryWorkId}/work_complete_check_regist`, {
    t_temporary_work: {
      confirm_worked: temporaryWorkData.confirm_worked,
      confirm_worked_dt: temporaryWorkData.confirm_worked_dt,
      work_dt: temporaryWorkData.work_dt
    },
  });
};

// 作業未実施登録
export const workUnworkedRegist = (temporaryWorkId) => {
  return instance.put(`/t_temporary_works/${temporaryWorkId}/work_unworked_regist`);
};

// 作業未実施取消
export const workUnworkedCancel = (temporaryWorkId) => {
  return instance.put(`/t_temporary_works/${temporaryWorkId}/work_complete_cancel_regist`);
};

// 作業実施確認解除
export const workCompleteCancel = (temporaryWorkId) => {
  return instance.put(`/t_temporary_works/${temporaryWorkId}/work_complete_cancel_regist`);
};
// 臨時業務情報API＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝


// 送付表関連API ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
// ************** 請負業者予定表 **************
// 請負業者一覧
export const getMonthlyWorkVendors = (params = {}) => {
  return instance.get('/t_vendor_work_monthly_calendars', { params });
};

// 新規作成用情報取得
export const getVendorMonthlyWorkSchedules = (vendorId, year, month) => {
  return instance.get('/t_vendor_work_monthly_calendars/vendor_monthly_works', {
    params: {
      vendor_id: vendorId,
      year: year,
      month: month
    }
  });
};

// 請負業者予定表登録
export const createVendorMonthlyWorkSchedules = (monthlyScheduleData) => {
  return instance.post('/t_vendor_work_monthly_calendars', {
    t_vendor_work_monthly_calendar: monthlyScheduleData
  });
};

// 請負業者予定表詳細取得
export const getVendorMonthlyWorkScheduleDetails = (vendorId, year, month) => {
  return instance.get('/t_vendor_work_monthly_calendars/vendor_monthly_work', {
    params: {
      vendor_id: vendorId,
      year: year,
      month: month
    }
  });
};

// 請負業者予定表編集用情報取得
export const getVendorMonthlyWorkSchedule = (vendorMonthlyWorkScheduleId, vendorId, year, month) => {
  return instance.get(`/t_vendor_work_monthly_calendars/${vendorMonthlyWorkScheduleId}`,{
    params: {
      vendor_id: vendorId,
      year: year,
      month: month
    }
  });
};

// 請負業者予定表更新
export const updateVendorMonthlyWorkSchedule = (vendorMonthlyWorkScheduleId, monthlyScheduleData) => {
  return instance.put(`/t_vendor_work_monthly_calendars/${vendorMonthlyWorkScheduleId}`, {
    t_vendor_work_monthly_calendar: monthlyScheduleData
  });
};
// ************** 請負業者予定表 **************

// ************** 顧客予定表 **************
// 顧客予定表一覧
export const getCustomerCalendars = (params = {}) => {
  return instance.get('/t_customer_work_monthly_calendars', { params });
};

// 翌月の予定表未作成の建物の一覧
export const getNonCreateCalendarWorkBuildings = (params = {}) => {
  return instance.get('/t_customer_work_monthly_calendars/non_create_calendar_works', { params });
};

// 顧客単位予定表の新規作成用情報取得
export const getNewMonthlyWorkSchedules = (year, month, annualWorkIds) => {
  return instance.get('/t_customer_work_monthly_calendars/new_building_monthly_works', {
    params: {
      year: year,
      month: month,
      annual_work_ids: annualWorkIds
    }
  });
};

// 顧客予定表登録
export const createCustomerCalendars = (monthlyScheduleData) => {
  return instance.post('/t_customer_work_monthly_calendars', {
    t_annual_work_monthly_calendar: monthlyScheduleData
  });
};

// 顧客予定表詳細取得
export const getCustomerCalendarDetails = (buildingId, year, month) => {
  return instance.get('/t_customer_work_monthly_calendars/building_monthly_work', {
    params: {
      building_id: buildingId,
      year: year,
      month: month
    }
  });
};

// 顧客予定表編集用情報取得
export const getCustomerCalendar = (workCalendarId, year, month) => {
  return instance.get(`/t_customer_work_monthly_calendars/${workCalendarId}`,{
    params: {
      year: year,
      month: month
    }
  });
};

// 顧客予定表更新
export const updateCustomerCalendar = (workCalendarId, monthlyScheduleData) => {
  return instance.put(`/t_customer_work_monthly_calendars/${workCalendarId}`, {
    t_annual_work_monthly_calendar: monthlyScheduleData
  });
};

// 顧客予定表削除
export const deleteCustomerCalendar = (customerCalendarId) => {
  return instance.delete(`/t_customer_work_monthly_calendars/${customerCalendarId}`);
};

// 顧客予定表PDFステータス更新
export const updateCustomerCalendarPdfStatus = (workCalendarId, year, month) => {
  return instance.put(`/t_customer_work_monthly_calendars/${workCalendarId}/update_pdf_status`, {
    year: year,
    month: month,
    pdf_output_completed: true,
    pdf_latest_output_datetime: new Date().toISOString()
  });
};
// ************** 顧客予定表 **************
// 送付表関連API ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝


// 請求管理関連API ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
// ************** 年間予定作業と臨時作業の請求書 **************
// 請求書一覧（初期表示は当月）
export const getMonthlyBillings = (params = {}) => {
  return instance.get('/t_monthly_billings', { params });
};
// 当月の未請求の年間作業と臨時作業の一覧
export const getNonMonthlyBillings = (params = {}) => {
  return instance.get('/t_monthly_billings/non_billing_works', { params });
};

// 顧客単位請求書の新規作成用情報取得
export const getNewMonthlyBillings = (year, month, annualWorkIds, temporalyWorkIds) => {
  return instance.get('/t_monthly_billings/new_building_monthly_works', {
    params: {
      year: year,
      month: month,
      annual_work_ids: annualWorkIds,
      temporaly_work_ids: temporalyWorkIds,
    }
  });
};

// 顧客単位請求書情報登録
export const createMonthlyBillings = (monthlyBillingData) => {
  return instance.post('/t_monthly_billings', {
    t_customer_monthly_billing: monthlyBillingData
  });
};

// 請求情報取得（詳細/編集表示用）
export const getMonthlyBilling = (MonthlyBillingId, year, month) => {
  return instance.get(`/t_monthly_billings/${MonthlyBillingId}`,{
    params: {
      year: year,
      month: month
    }
  });
};

// 請求情報更新
export const updateMonthlyBillings = (MonthlyBillingId, monthlyBillingData) => {
  return instance.put(`/t_monthly_billings/${MonthlyBillingId}`, {
    t_customer_monthly_billing: monthlyBillingData
  });
};

// 請求情報PDF出力ステータス更新
export const updateBuildingMonthlyWorkBillingPdfStatus = (buildingMonthlyWorkScheduleId) => {
  return instance.put(`/t_monthly_billings/${buildingMonthlyWorkScheduleId}/update_pdf_status`, {
    pdf_output_completed: true,
    pdf_latest_output_datetime: new Date().toISOString()
  });
};

// 請求情報削除
export const deleteMonthlyBilling = (MonthlyBillingId) => {
  return instance.delete(`/t_monthly_billings/${MonthlyBillingId}`);
};
// ************** 年間予定作業と臨時作業の請求書 **************

// ************** 年間作業予定請求書 **************
// 請求書メール送信
export const sendBuildingMonthlyWorkBilling = (formData) => {
  return instance.post('/t_building_annual_work_monthly_billings/send_email', formData, {
    headers: { "Content-Type": "multipart/form-data" }  // ✅ headers を 3 つ目の引数で指定
  });
};
// ************** 年間作業予定請求書 **************
// 請求管理関連API ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝


// 顧客金額関連API ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
// 顧客金額情報取得（全顧客）
export const getCustomerMoneyConditions = (params = {}) => {
  return instance.get(`/t_customer_money_conditions`, { params });
};

// 顧客単位請求書の新規作成用情報取得
export const getCustomerMonthlyBillings = (customerId, year, month) => {
  return instance.get('/t_customer_money_conditions/customer_monthly_billings', {
    params: {
      customer_id: customerId,
      year: year,
      month: month
    }
  });
};

// 繰越金額取得（年間予定作業請求書と臨時作業請求書で使用）
export const getCustomerUnconfirmedAmount = (params = {}) => {
  return instance.get('/t_customer_money_conditions/get_customer_unconfirmed_amount', { params });
};

// 入金確認情報履歴取得
export const getCustomerPaymentConfirmHistories = (params = {}) => {
  return instance.get(`/t_customer_money_conditions/get_payment_confirm_histories`, { params });
};

// 入金確認情報更新
export const registCustomerPaymentConfirm = (monthlyConfirmInvoiceData) => {
  return instance.put(`/t_customer_money_conditions/regist_payment_confirm`, {
    t_customer_money_condition: {
      customer_id: monthlyConfirmInvoiceData.customer_id,
      year: monthlyConfirmInvoiceData.year,
      month: monthlyConfirmInvoiceData.month,
      payment_confirm_dt: monthlyConfirmInvoiceData.payment_confirm_dt,
      payment_confirm_amount: monthlyConfirmInvoiceData.payment_confirm_amount,
      transfer_fee: monthlyConfirmInvoiceData.transfer_fee,
      other_deduction_amount: monthlyConfirmInvoiceData.other_deduction_amount,
      other_deduction_description: monthlyConfirmInvoiceData.other_deduction_description,
    },
  });
};

// 入金確認履歴更新
export const updateCustomerPaymentConfirmHistory = (historyId, updateData) => {
  return instance.put(`/t_customer_money_conditions/update_payment_confirm_history`, {
    id: historyId,
    t_customer_money_condition: {
      payment_confirm_dt: updateData.payment_confirm_dt,
      payment_confirm_amount: updateData.payment_confirm_amount,
      transfer_fee: updateData.transfer_fee,
      other_deduction_amount: updateData.other_deduction_amount,
      other_deduction_description: updateData.other_deduction_description,
      edit_reason: updateData.edit_reason,
    },
  });
};

// 入金確認データリセット確認
export const getCustomerPaymentConfirmResetSummary = (params = {}) => {
  return instance.get('/customer_payment_confirm_reset', { params });
};

// 入金確認データリセット実行
export const executeCustomerPaymentConfirmReset = (params = {}) => {
  return instance.post('/customer_payment_confirm_reset', params);
};
// 顧客金額関連API ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝


// 集計情報管理API ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝
// ************** 年間作業予定 売上情報集計 **************
// 建物別売上情報集計
export const getAggregateSalesByBuilding = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_sales_by_building', { params });
};

// 顧客別売上情報集計
export const getAggregateSalesByCustomer = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_sales_by_customer', { params });
};

// 業者別売上情報集計
export const getAggregateSalesByVendor = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_sales_by_vendor', { params });
};

// 業務区分別売上情報集計
export const getAggregateSalesByBusinessClassification = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_sales_by_business_classification', { params });
};

// 担当者別売上情報集計
export const getAggregateSalesByInCharge = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_sales_by_in_charge', { params });
};

// 月別売上情報集計
export const getAggregateSalesByMonth = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_sales_by_month', { params });
};
// ************** 年間作業予定 売上情報集計 **************


// ************** 臨時作業 売上情報集計 **************
// 建物別売上情報集計
export const getAggregateTemporaryWorkSalesByBuilding = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_temporary_work_sales_by_building', { params });
};

// 顧客別売上情報集計
export const getAggregateTemporaryWorkSalesByCustomer = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_temporary_work_sales_by_customer', { params });
};

// 業者別売上情報集計
export const getAggregateTemporaryWorkSalesByVendor = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_temporary_work_sales_by_vendor', { params });
};

// 業務区分別売上情報集計
export const getAggregateTemporaryWorkSalesByBusinessClassification = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_temporary_work_sales_by_business_classification', { params });
};

// 担当者別売上情報集計
export const getAggregateTemporaryWorkSalesByInCharge = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_temporary_work_sales_by_in_charge', { params });
};

// 月別売上情報集計
export const getAggregateTemporaryWorkSalesByMonth = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_temporary_work_sales_by_month', { params });
};
// ************** 臨時作業 売上情報集計 **************


// ************** 全作業 売上情報集計 **************
// 建物別売上情報集計
export const getAggregateTotalSalesByBuilding = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_total_sales_by_building', { params });
};

// 顧客別売上情報集計
export const getAggregateTotalSalesByCustomer = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_total_sales_by_customer', { params });
};

// 業者別売上情報集計
export const getAggregateTotalSalesByVendor = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_total_sales_by_vendor', { params });
};

// 業務区分別売上情報集計
export const getAggregateTotalSalesByBusinessClassification = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_total_sales_by_business_classification', { params });
};

// 担当者別売上情報集計
export const getAggregateTotalSalesByInCharge = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_total_sales_by_in_charge', { params });
};

// 月別売上情報集計
export const getAggregateTotalSalesByMonth = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_total_sales_by_month', { params });
};
// ************** 全作業 売上情報集計 **************


// ************** 売上情報集計 **************
// 年を指定して業者の年間作業情報を取得する関数
export const getVendorAnnualSchedules = (params = {}) => {
  return instance.get('/aggregate_informations/aggregate_vendor_work_history', { params });
};
// ************** 売上情報集計 **************
// 集計情報管理API ＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝＝



// すべてのリクエストに認証ヘッダーを添付
instance.interceptors.request.use(config => {
  if (shouldTrackGlobalLoading(config)) {
    store.dispatch('startGlobalLoading');
  }

  config.headers['X-Client-App'] = 'billing';

  if (hasStoredAuth()) {
    config.headers['access-token'] = localStorage.getItem('accessToken');
    config.headers['client'] = localStorage.getItem('client');
    config.headers['uid'] = localStorage.getItem('uid');
  }

  return config;
}, error => {
  if (error?.config && shouldTrackGlobalLoading(error.config)) {
    store.dispatch('stopGlobalLoading');
  }
  return Promise.reject(error);
});

// レスポンスインターセプター
// 応答から新しいトークンを取得して更新
instance.interceptors.response.use(response => {
  if (shouldTrackGlobalLoading(response.config)) {
    store.dispatch('stopGlobalLoading');
  }

  saveAuthHeaders(response.headers);

  return response;
}, error => {
  if (error?.config && shouldTrackGlobalLoading(error.config)) {
    store.dispatch('stopGlobalLoading');
  }

  // 401 Unauthorizedエラーの場合、ユーザーをログアウト処理
  if (error.response && (error.response.status === 401 || isSystemAccessDenied(error))) {
    clearAuthStorage();
    syncStoreAuthState(false);
  }
  return Promise.reject(error);
});

export default instance;
